import { Button, Card, Loader, Stack, Text, Title } from '@mantine/core'
import { useEffect, useRef, useState } from 'react'

import { appLocalLogo } from 'src/common/libs'

import { authApiEndpoints } from '../../api/auth.api'
import {
  resetAuthStore,
  setAuthInfo,
  setAuthStatus,
} from '../../store/auth.store'
import { isCookieAuthenticationEnabled } from '../../utils/isCookieAuthenticationEnabled'

import { checkRoute } from '@v2/domains/routing/actions/routing.actions'
import type { RestorableSessionProfile } from '@v2/domains/user/session.mapper'
import {
  resetUserSessionStore,
  setUserSession,
} from '@v2/domains/user/store/user.store'
import { useAppDispatch, useAppSelector } from '@v2/store/hooks'

interface AuthSessionBootstrapProps {
  children: React.ReactNode
}

const PASSWORD_RECOVERY_PATHS = new Set(['/forgot', '/reset-password'])
const SELECT_ORGANIZATION_PATH = '/select-organization'

const isSessionAuthenticationFailure = (error: unknown) =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  (error.status === 401 || error.status === 403)

/**
 * Restore authoritative profile and organization data before route guards run.
 * The organization selector supports sessions with no organization context.
 */
export const AuthSessionBootstrap = ({
  children,
}: AuthSessionBootstrapProps) => {
  const dispatch = useAppDispatch()
  const authStatus = useAppSelector((state) => state.auth.status)
  const legacyRefreshToken = useAppSelector((state) => state.auth.refreshToken)
  const legacyUserId = useAppSelector((state) => state.auth.userId)
  const legacyOrganizationId = useAppSelector(
    (state) => state.auth.organizationId,
  )
  const legacyMembershipId = useAppSelector((state) => state.auth.membershipId)
  const pathname = window.location.pathname
  const isPasswordRecoveryPath = PASSWORD_RECOVERY_PATHS.has(pathname)
  const isPreOrganizationPath = pathname === SELECT_ORGANIZATION_PATH
  const initialSession = useRef({
    authStatus,
    legacyRefreshToken,
    legacyUserId,
    legacyOrganizationId,
    legacyMembershipId,
    isPasswordRecoveryPath,
    isPreOrganizationPath,
  })
  const [isRestoring, setIsRestoring] = useState(
    authStatus === 'unknown' && !isPasswordRecoveryPath,
  )
  const [restorationFailed, setRestorationFailed] = useState(false)
  const [retryAttempt, setRetryAttempt] = useState(0)
  const restoredSelectorProfile = useRef<RestorableSessionProfile | null>(null)

  useEffect(() => {
    const {
      authStatus,
      legacyRefreshToken,
      legacyUserId,
      legacyOrganizationId,
      legacyMembershipId,
      isPasswordRecoveryPath,
      isPreOrganizationPath,
    } = initialSession.current

    if (authStatus !== 'unknown') {
      return
    }

    if (isPasswordRecoveryPath) {
      dispatch(setAuthStatus('unauthenticated'))
      dispatch(checkRoute({ skipValidation: false }))
      return
    }

    let isMounted = true

    const restoreSession = async () => {
      //debugger
      let restorationStep: 'refresh' | 'profile' | 'organizations' = 'refresh'
      let isRetryableFailure = false

      try {
        // Legacy credentials are the only browser-persisted auth exception.
        // Refresh before profile requests so an expired access token cannot
        // trigger the legacy 401 interceptor while restoring the session.
        // Refresh requires the existing tenant IDs to retain organization
        // context in the new access token and subsequent profile response.
        const refreshed =
          !isCookieAuthenticationEnabled && legacyRefreshToken
            ? await dispatch(
                authApiEndpoints.refreshToken.initiate({
                  refreshToken: legacyRefreshToken,
                  ...(legacyOrganizationId && {
                    organizationId: legacyOrganizationId,
                  }),
                  ...(legacyMembershipId && {
                    membershipId: legacyMembershipId,
                  }),
                }),
              ).unwrap()
            : null

        if (isPreOrganizationPath && !isCookieAuthenticationEnabled) {
          const userId = refreshed?.userId || legacyUserId

          if (!userId) {
            throw new Error('Session did not include a user ID')
          }

          restorationStep = 'organizations'

          const organizationsResponse = await dispatch(
            authApiEndpoints.getOrganizationsByUserId.initiate(userId, {
              forceRefetch: true,
              subscribe: false,
            }),
          ).unwrap()

          if (!isMounted) {
            return
          }

          dispatch(setAuthInfo({ userId }))
          dispatch(
            setUserSession({ organizations: organizationsResponse.records }),
          )
          return
        }

        restorationStep = 'profile'
        const isCookieSelector =
          isCookieAuthenticationEnabled && isPreOrganizationPath
        // A valid limited session can load the selector without refreshing.
        // Axios refreshes and retries only if this profile request returns 401.
        const profile = isCookieSelector
          ? restoredSelectorProfile.current ??
            (await dispatch(
              authApiEndpoints.getAuthSessionProfile.initiate(null, {
                forceRefetch: true,
                subscribe: false,
              }),
            ).unwrap())
          : await dispatch(
              authApiEndpoints.getAuthProfile.initiate(null, {
                forceRefetch: true,
                subscribe: false,
              }),
            ).unwrap()

        if (isCookieSelector) {
          const hasOrganization = Boolean(profile.organization?.id)
          const hasMembership = Boolean(profile.membership?.id)
          if (
            !profile.user.id ||
            hasOrganization !== hasMembership ||
            (profile.organization !== null && !hasOrganization) ||
            (profile.membership !== null && !hasMembership)
          ) {
            throw new Error(
              'Session profile has incomplete user or organization context',
            )
          }
          // Organization-list retries reuse the sanitized profile in memory.
          restoredSelectorProfile.current = profile
          if (!isMounted) {
            return
          }
          // If access expires during organization loading, automatic refresh
          // must retain any organization context established by the profile.
          dispatch(
            setAuthInfo({
              userId: profile.user.id,
              ...(profile.organization &&
                profile.membership && {
                  organizationId: profile.organization.id,
                  membershipId: profile.membership.id,
                }),
            }),
          )
        }
        restorationStep = 'organizations'

        const organizationsResponse = await dispatch(
          authApiEndpoints.getOrganizationsByUserId.initiate(profile.user.id, {
            forceRefetch: true,
            subscribe: false,
          }),
        ).unwrap()

        if (!isMounted) {
          return
        }

        dispatch(
          setAuthInfo({
            userId: profile.user.id,
            ...(profile.organization &&
              profile.membership && {
                organizationId: profile.organization.id,
                membershipId: profile.membership.id,
              }),
          }),
        )
        dispatch(
          setUserSession({
            user: profile.user,
            ...(profile.organization &&
              profile.membership && {
                currentOrganization: profile.organization,
                membership: profile.membership,
              }),
            policies: profile.policies,
            organizations: organizationsResponse.records,
          }),
        )
        if (profile.organization) {
          appLocalLogo.setData('logo', profile.organization.circularLogoURL)
        }
      } catch (error) {
        if (isMounted) {
          isRetryableFailure =
            isCookieAuthenticationEnabled &&
            isPreOrganizationPath &&
            !(
              restorationStep === 'profile' &&
              isSessionAuthenticationFailure(error)
            )

          if (isRetryableFailure) {
            setRestorationFailed(true)
          } else {
            dispatch(resetAuthStore())
            dispatch(resetUserSessionStore())
          }
        }
      } finally {
        if (isMounted) {
          if (!isRetryableFailure) {
            dispatch(checkRoute({ skipValidation: false }))
          }
          setIsRestoring(false)
        }
      }
    }

    // StrictMode replays effects on mount. Start after that replay so session
    // restoration does not start two concurrent request sequences.
    queueMicrotask(() => {
      if (isMounted) {
        void restoreSession()
      }
    })

    return () => {
      isMounted = false
    }
  }, [dispatch, retryAttempt])

  if (restorationFailed) {
    return (
      <Stack align="center" justify="center" mih="100dvh" p="lg">
        <Card
          data-testid="select-organization-session-recovery-card"
          withBorder
          p="xl"
          maw={440}
        >
          <Stack>
            <Title order={1} size="h3">
              Unable to load your organizations
            </Title>
            <Text
              role="alert"
              data-testid="select-organization-session-recovery-label"
            >
              We couldn't finish restoring your session. Please try again.
            </Text>
            <Button
              data-testid="select-organization-session-retry-button"
              onClick={() => {
                setRestorationFailed(false)
                setIsRestoring(true)
                setRetryAttempt((attempt) => attempt + 1)
              }}
            >
              Retry
            </Button>
          </Stack>
        </Card>
      </Stack>
    )
  }

  if (isRestoring) {
    if (isCookieAuthenticationEnabled && isPreOrganizationPath) {
      return (
        <Stack
          align="center"
          justify="center"
          mih="100dvh"
          role="status"
          data-testid="select-organization-session-loading-label"
        >
          <Loader aria-hidden="true" />
          <Text>Restoring your session…</Text>
        </Stack>
      )
    }
    return null
  }

  return children
}
